import { useSelector } from 'react-redux';
import { Campaign } from '@shared/types/mylounge';
import { getCustomerGroup } from '@client/redux/modules/customer';
import { LoungVisitorActivitySegments } from '@shared/types/customer';

import { useNewDealsData } from './useNewDealsData';
import { CAMPAIGN_COUNT_CONSTANTS } from '../../campaignsCarousel/helpers';

/**
 * Return type for useNewDeals hook
 * Provides data and flags needed for New Deals display
 *
 * For granular variant control (isNewDealsTwoRowGrid, isNewDealsOneRowGrid),
 * import and use useNewDealsVariants() directly
 */
export interface UseNewDealsReturn {
    // Core variant flags
    isNewDeals: boolean;
    // Business logic flags
    isNewDealFeatureEnabled: boolean;
    // Campaign data
    newDealsCampaigns: Campaign[];
    campaignsForNewDeal: Campaign[] | undefined;
}

/**
 * Custom hook for managing New Deals Today functionality
 * Orchestrates variant detection, data fetching, and campaign selection
 *
 * Combines:
 * - useNewDealData() for campaign data
 * - Business logic for tab activity and carousel display
 * - Layout-aware campaign count selection
 *
 * @returns {UseNewDealsReturn} Complete new deals data and state
 */
export const useNewDeals = (): UseNewDealsReturn => {
    // Get campaign data
    const { newDealsCampaigns } = useNewDealsData();

    const customerGroup = useSelector(getCustomerGroup);
    const isDailyUser = !!(
        customerGroup &&
        customerGroup.lounge_visitor_activity_segments === LoungVisitorActivitySegments.DAILY
    );
    // Determine if feature should be shown
    const hasAtLeastEightCampaigns =
        newDealsCampaigns.length >= CAMPAIGN_COUNT_CONSTANTS.EIGHT_CAMPAIGNS_CAROUSEL;
    const isNewDealFeatureEnabled = isDailyUser && hasAtLeastEightCampaigns;

    // Slice campaigns for carousel (max 8 for leaped variant)
    const campaignsForNewDeal = isDailyUser
        ? newDealsCampaigns.slice(0, CAMPAIGN_COUNT_CONSTANTS.EIGHT_CAMPAIGNS_CAROUSEL)
        : undefined;

    return {
        // Core variant flags
        isNewDeals: isDailyUser,
        // Business logic flags
        isNewDealFeatureEnabled,
        // Campaign data
        newDealsCampaigns,
        campaignsForNewDeal,
    };
};
